import React, { useEffect } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { t } from '@/i18n';
import { colors, radius } from '@/theme';
import { useData } from '@/useData';
import { ErrorNote, Loading, Pill, T } from '@/ui';

export type CallAgent = { id: number; name: string; phone: string | null; to_call: number };

/**
 * Who calls the customer when forwarding (backend: OrderOps::callAgents): one call-center agent
 * of the order's governorate, least busy first and preselected, or 0 = all of them (the first
 * one to record a call takes the order).
 */
export function CallAgentPicker({ orderId, value, onChange }: { orderId: number; value: number | undefined; onChange: (id: number) => void }) {
  const { data, error, loading } = useData<{ agents: CallAgent[]; current: number | null }>(`/ops/orders/${orderId}/call-agents`);
  const agents = data?.agents ?? [];

  useEffect(() => {
    if (data && value === undefined) onChange(agents[0]?.id ?? 0);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !data) return <Loading />;
  if (!data) return <ErrorNote text={error} />;

  return (
    <View style={{ gap: 8 }}>
      <T size={12} color={colors.muted}>{t('مين يتصل بالزبون؟', 'Who calls the customer?')}</T>
      {agents.map((a, i) => (
        <Option key={a.id} active={value === a.id} onPress={() => onChange(a.id)}
          title={a.name}
          sub={t(`عنده ${a.to_call} طلب للاتصال`, `${a.to_call} orders to call`, `${a.to_call} داواکاری بۆ پەیوەندی لایەتی`)}
          badge={data.current === a.id ? t('الحالي', 'Current') : i === 0 && agents.length > 1 ? t('الأقل ضغطاً', 'Least busy') : undefined} />
      ))}
      <Option active={value === 0} onPress={() => onChange(0)} icon="people-outline"
        title={agents.length ? t(`كل موظفي الكول سنتر (${agents.length})`, `All call-center agents (${agents.length})`, `هەموو فەرمانبەرانی کۆڵ سەنتەر (${agents.length})`) : t('الكل', 'Everyone')}
        sub={agents.length
          ? t('يصل للكل، وأول واحد يسجّل نتيجة الاتصال بيستلم الطلب', 'Sent to all; the first to record a call takes the order')
          : t('لا يوجد موظف كول سنتر لهذه المحافظة', 'No call-center agent for this governorate')} />
    </View>
  );
}

function Option({ active, onPress, title, sub, badge, icon }: {
  active: boolean; onPress: () => void; title: string; sub: string; badge?: string; icon?: keyof typeof Ionicons.glyphMap;
}) {
  return (
    <Pressable onPress={onPress} style={[st.row, active && st.rowOn]}>
      <Ionicons name={active ? 'radio-button-on' : 'radio-button-off'} size={20} color={colors.violet} />
      <View style={{ flex: 1 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          {icon ? <Ionicons name={icon} size={16} color={colors.violet} /> : null}
          <T w="bold">{title}</T>
          {badge ? <Pill label={badge} bg={colors.turquoiseTint} fg={colors.turquoiseDark} /> : null}
        </View>
        <T size={12} color={colors.muted}>{sub}</T>
      </View>
    </Pressable>
  );
}

const st = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1.5, borderColor: colors.border, borderRadius: radius.md, padding: 10 },
  rowOn: { borderColor: colors.violet, backgroundColor: colors.violetTint },
});
